import Calendar from '@ixigo/icons/Calendar';
import { InlineAlert } from '@ixigo/iui/InlineAlert';
import Input from '@ixigo/iui/Input';
import Typography from '@ixigo/iui/Typography';
import { useMemo, useRef, useState } from 'react';
import type { RideDetails } from '@/api';
import {
	DateTimePickerSheet,
	formatDateTimeLabel,
} from '@/components/date-time-picker';
import {
	fetchTripType,
	type TripTypeResponse,
} from '@/components/search-form/api';
import { SectionHeader } from '@/components/section-header';

interface ModifyDetailProps {
	rideDetails: RideDetails;
	note?: string;
	currentDateTime: string;
	onContinue?: (newDateTime?: string) => void;
}

export const ModifyDetail = ({
	rideDetails,
	note,
	currentDateTime,
	onContinue,
}: ModifyDetailProps) => {
	const [showDateTimePicker, setShowDateTimePicker] = useState(false);
	const [tripTypeInfo, setTripTypeInfo] = useState<
		TripTypeResponse['data'] | null
	>(null);
	const isFocusedRef = useRef(false);

	const openDateTimePicker = () => {
		const { pickup, drop } = rideDetails;
		fetchTripType({
			pickup: { ...pickup.location, type: pickup.type },
			drop: { ...drop.location, type: drop.type },
		}).then((response) => {
			if (!('errors' in response)) {
				setTripTypeInfo(response.data);
			}
		});
		setShowDateTimePicker(true);
		isFocusedRef.current = true;
	};

	const closeDateTimePicker = () => {
		isFocusedRef.current = false;
		setShowDateTimePicker(false);
	};

	const formattedDateTime = useMemo(
		() => formatDateTimeLabel(currentDateTime),
		[currentDateTime],
	);

	return (
		<div className="bg-primary p-20 flex flex-col gap-y-15">
			<SectionHeader title="Modify Pickup Time" />
			{/* biome-ignore lint/a11y/noStaticElementInteractions: readOnly input wrapper opens DateTimePickerSheet */}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard interaction handled by DateTimePickerSheet */}
			<div onClick={openDateTimePicker}>
				<Input
					variant="outlined"
					label="Date & Time"
					placeholder="Select Pickup Date & Time"
					value={formattedDateTime}
					className="w-full"
					focused={isFocusedRef.current}
					readOnly
					endAdornment={<Calendar />}
				/>
			</div>
			{note && (
				<InlineAlert
					title={
						<Typography
							variant="body-sm"
							/** biome-ignore lint/security/noDangerouslySetInnerHtml: <note is safe html string> */
							dangerouslySetInnerHTML={{ __html: note }}
						/>
					}
					severity="neutral"
					textColor="neutral"
				/>
			)}
			<DateTimePickerSheet
				open={showDateTimePicker}
				onClose={closeDateTimePicker}
				minDurationInMinutes={tripTypeInfo?.minDurationInMinutes}
				alertMsg={tripTypeInfo?.msg}
				defaultValue={currentDateTime}
				onConfirm={(value) => {
					onContinue?.(value.toISOString());
					if (value.getTime() !== new Date(currentDateTime).getTime()) {
						closeDateTimePicker();
					}
				}}
			/>
		</div>
	);
};
